/* ==========================================================================
   Multiple-collection filtering controller.

   Filters the stacked sub-collection blocks on the collection.multiple-collection
   template. Because the parent is a combined (multi-source) collection, native
   ?filter.* / sort_by faceting runs against its aggregated products; the section
   (snippets/multi-collection-block.liquid) buckets those filtered products back
   under each sub-collection heading.

   On any filter/sort change we fetch this section via the Section Rendering API
   and swap the product blocks + facet counts + active pills, with no page reload.

   Docs: docs/multiple-collection-filter.md
   ========================================================================== */

(function () {
  var debounceFn =
    typeof window.debounce === 'function'
      ? window.debounce
      : function (fn, wait) {
          var t;
          return function () {
            var ctx = this;
            var args = arguments;
            clearTimeout(t);
            t = setTimeout(function () {
              fn.apply(ctx, args);
            }, wait);
          };
        };

  if (customElements.get('multi-collection-filters')) return;

  class MultiCollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.cache = {};
      this.sectionId = this.dataset.sections;
      this.filterPosition = this.dataset.filterPosition;
    }

    connectedCallback() {
      this.form = this.querySelector('#MultiCollectionFiltersForm');
      if (!this.form) return;

      // Registered before the debounced submit so the price fields are in sync by the time
      // the query string is built. Delegated, so it survives facet re-renders.
      this.form.addEventListener('input', this.onPriceInput.bind(this));

      this.debouncedSubmit = debounceFn(this.onFormInput.bind(this), 500);
      this.form.addEventListener('input', this.debouncedSubmit);

      // Delegate active-facet / clear-all removal (survives DOM swaps).
      this.addEventListener('click', this.onRemoveClick.bind(this));

      // "Show all / Show less" on long lists (vertical sidebar). Delegated; the expanded
      // state lives on the <details>, which is kept when its contents are re-rendered.
      this.addEventListener('click', this.onMoreClick.bind(this));

      window.addEventListener('popstate', this.onPopState.bind(this));

      // Drawer behaviour is available in every layout — it's the mobile presentation for
      // horizontal/vertical, and the desktop presentation for the drawer layout.
      if (window.theme && theme.Drawers && this.querySelector('#FilterDrawer')) {
        this.drawer = new theme.Drawers('FilterDrawer', 'filter');
        // theme.Drawers only wires the first .js-drawer-close; make every one close.
        this.querySelectorAll('.js-drawer-close').forEach(
          function (btn) {
            btn.addEventListener('click', function () {
              if (this.drawer) this.drawer.close();
            }.bind(this));
          }.bind(this)
        );
      }

      if (this.filterPosition === 'horizontal') {
        this.initInlineDropdowns();
      }
    }

    /* Horizontal (desktop): only one dropdown open at a time; click outside closes them. */
    initInlineDropdowns() {
      var panel = this.querySelector('#FilterDrawer .mc-filter-panel__body');
      if (!panel) return;

      panel.querySelectorAll('details.js-filter').forEach(function (details) {
        details.addEventListener('toggle', function () {
          if (!details.open) return;
          panel.querySelectorAll('details.js-filter').forEach(function (other) {
            if (other !== details) other.open = false;
          });
        });
      });

      document.addEventListener('click', function (event) {
        if (event.target.closest('.mc-filter-panel')) return;
        if (event.target.closest('.mc-filters__open')) return;
        panel.querySelectorAll('details.js-filter[open]').forEach(function (details) {
          details.open = false;
        });
      });
    }

    /* Price facet: keep the dual-thumb slider and the min/max number fields in sync.
       A thumb resting on its bound clears the matching field so no filter is applied. */
    onPriceInput(event) {
      var target = event.target;
      var price = target && target.closest ? target.closest('.js-price-range') : null;
      if (!price) return;

      var minRange = price.querySelector('.js-price-range-min');
      var maxRange = price.querySelector('.js-price-range-max');
      var minInput = price.querySelector('.js-price-input-min');
      var maxInput = price.querySelector('.js-price-input-max');
      if (!minRange || !maxRange || !minInput || !maxInput) return;

      var limit = parseFloat(maxRange.max) || 0;
      if (limit <= 0) return;
      var gap = Math.min(1, limit);
      var lo;
      var hi;

      if (target === minRange || target === maxRange) {
        lo = parseFloat(minRange.value);
        hi = parseFloat(maxRange.value);
        // Thumbs cannot cross.
        if (hi - lo < gap) {
          if (target === minRange) lo = Math.max(0, hi - gap);
          else hi = Math.min(limit, lo + gap);
        }
        minInput.value = lo > 0 ? lo : '';
        maxInput.value = hi < limit ? hi : '';
      } else {
        lo = parseFloat(minInput.value);
        hi = parseFloat(maxInput.value);
        if (isNaN(lo)) lo = 0;
        if (isNaN(hi)) hi = limit;
        lo = Math.min(Math.max(lo, 0), limit);
        hi = Math.min(Math.max(hi, lo), limit);
      }

      minRange.value = lo;
      maxRange.value = hi;
      price.style.setProperty('--mc-range-lo', (lo / limit) * 100 + '%');
      price.style.setProperty('--mc-range-hi', (hi / limit) * 100 + '%');
      // Keep the min thumb reachable when both thumbs sit at the top of the range.
      minRange.style.zIndex = lo > limit / 2 ? '2' : '';

      var facet = price.closest('.js-filter');
      var dot = facet ? facet.querySelector('.js-price-dot') : null;
      if (dot) dot.hidden = !(lo > 0 || hi < limit);
    }

    onFormInput(event) {
      if (event) event.preventDefault();
      this.renderPage(this.currentParams());
    }

    onRemoveClick(event) {
      var link = event.target.closest('.js-facet-remove');
      if (!link) return;
      event.preventDefault();
      var params = new URL(link.href, window.location.origin).searchParams.toString();
      this.renderPage(params);
    }

    onMoreClick(event) {
      var button = event.target.closest('.js-facet-more');
      if (!button) return;
      var facet = button.closest('.js-filter');
      if (!facet) return;
      var expanded = facet.classList.toggle('mc-facet--expanded');
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    onPopState(event) {
      var params = event.state && event.state.mcParams != null ? event.state.mcParams : '';
      this.renderPage(params, false);
    }

    /* Build the ?filter.* / sort_by query string from the live form, dropping empties. */
    currentParams() {
      var formData = new FormData(this.form);
      var search = new URLSearchParams();
      formData.forEach(function (value, key) {
        if (value !== '' && value != null) search.append(key, value);
      });
      return search.toString();
    }

    renderPage(searchParams, updateUrl) {
      if (updateUrl !== false) this.pushState(searchParams);
      this.setLoading(true);

      var url =
        window.location.pathname + '?section_id=' + this.sectionId + (searchParams ? '&' + searchParams : '');

      if (this.cache[url]) {
        this.renderFrom(this.cache[url], searchParams);
        return;
      }

      fetch(url)
        .then(function (res) {
          return res.text();
        })
        .then(
          function (text) {
            this.cache[url] = text;
            this.renderFrom(text, searchParams);
          }.bind(this)
        )
        .catch(
          function () {
            // On failure fall back to a full navigation so the user still gets results.
            window.location.search = searchParams;
          }.bind(this)
        );
    }

    renderFrom(html, searchParams) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');

      this.renderBlocks(parsed);
      this.renderFacets(parsed);
      this.renderActiveFacets(parsed);
      this.renderOpenButton(parsed);

      this.setLoading(false);
    }

    renderBlocks(parsed) {
      var next = parsed.getElementById('MCProductBlocks');
      var current = document.getElementById('MCProductBlocks');
      if (next && current) current.innerHTML = next.innerHTML;
    }

    /* Re-render every facet group except the one the user is currently interacting with,
       so their in-progress selection keeps focus. */
    renderFacets(parsed) {
      var activeEl = document.activeElement;
      var activeFilter = activeEl && activeEl.closest ? activeEl.closest('.js-filter') : null;
      var activeIndex = activeFilter ? activeFilter.dataset.index : null;

      var nextFilters = parsed.querySelectorAll('#MultiCollectionFiltersForm .js-filter');
      nextFilters.forEach(function (nextFilter) {
        var index = nextFilter.dataset.index;
        if (index === activeIndex) return;
        var current = document.querySelector('.js-filter[data-index="' + index + '"]');
        if (!current) return;
        current.innerHTML = nextFilter.innerHTML;
        var more = current.querySelector('.js-facet-more');
        if (more && current.classList.contains('mc-facet--expanded')) more.setAttribute('aria-expanded', 'true');
      });
    }

    renderActiveFacets(parsed) {
      var next = parsed.querySelector('.mc-active-facets');
      var current = document.querySelector('.mc-active-facets');
      if (!next || !current) return;
      current.innerHTML = next.innerHTML;
      if (next.hasAttribute('hidden')) {
        current.setAttribute('hidden', '');
      } else {
        current.removeAttribute('hidden');
      }
    }

    /* Keep the "Filter (n)" trigger label in step with the number of applied filters. */
    renderOpenButton(parsed) {
      var next = parsed.querySelector('.mc-filters__open span');
      var current = this.querySelector('.mc-filters__open span');
      if (next && current) current.textContent = next.textContent;
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      var blocks = document.getElementById('MCProductBlocks');
      if (blocks) blocks.classList.toggle('mc-loading', isLoading);
    }

    pushState(searchParams) {
      var url = window.location.pathname + (searchParams ? '?' + searchParams : '');
      window.history.pushState({ mcParams: searchParams }, '', url);
    }
  }

  customElements.define('multi-collection-filters', MultiCollectionFilters);
})();
